/**
 * An uploaded or created plate playing live in the browser — with the same
 * animation code its videos are drawn with — in under a second: no video to
 * wait for ("Preparing animation…", "Writing sample words…"). Plays over and
 * over; with `words`, the sample words come into its panel. `look` tries a
 * motion, effects and a transition before they are saved.
 */

import React, { useState, useEffect } from 'react';
import { FiLoader } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { LivePlayer } from './PlatePackage';

export const PlateLive = ({ plateId, words = false, look = null, version, poster, className = 'absolute inset-0' }) => {
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [playKey, setPlayKey] = useState(0);
  const lookKey = JSON.stringify(look || {});

  useEffect(() => {
    let cancelled = false;
    setError('');
    // A change of look waits a moment (clicking through effects doesn't send each).
    const timer = setTimeout(async () => {
      try {
        // A look with a text area or blur areas goes in the body; a simple one as a link.
        const next = look && (look.box || look.blurs || look.font || look.effect || look.ink)
          ? await api.plateLiveLook(plateId, { words, ...look })
          : await api.plateLive(plateId, { words, ...(look || {}) });
        if (!cancelled) setData(next);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'error');
      }
    }, data ? 150 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line
  }, [plateId, words, lookKey, version]);

  return (
    <div className={className}>
      {poster && !data && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {data && <LivePlayer data={data} playKey={playKey} onEnd={() => setPlayKey((k) => k + 1)} />}
      {!data && !error && (
        <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/65 text-white text-[11px] px-2.5 py-0.5">
          <FiLoader size={11} className="animate-spin" /> {t('plateTemplates.live.loading')}
        </span>
      )}
      {error && <span className="absolute bottom-2 left-2 rounded-full bg-black/75 text-white text-[11px] px-2.5 py-0.5">{error}</span>}
    </div>
  );
};

export default PlateLive;
